import React, { useState } from 'react';
import SEO from './SEO';
import Container from './ui/Container';
import JobListing from './careers/JobListing';
import ApplicationForm from './forms/ApplicationForm';
import { COLORS } from '../constants';

const CAREERS_CONTENT = {
  title: 'Build your future with us!',
  introParagraphs: [
    'Welcome to our careers page.  We’re looking for bold thinkers, problem solvers, and innovators who want to make a real impact. Find your next role with us.',
    'Here you will find any current vacancies we are recruiting for, along with the relevant job descriptions.',
    'Should you not see any vacancies, we encourage you to submit your CV as we often recruit in advance of need.'
  ],
  whyWorkWithUsTitle: 'Why Work With Us',
  whyWorkWithUs: 'We firmly believe that our people make a real impact on our company’s performance. We operate in competitive sectors, which often means we are fast-paced and action-centred. Generally, you’ll find that no two days are the same, and therefore work can be interesting and varied. We welcome people who thrive with multitasking and enjoy a challenge, learn and grow and do their best work continually.',
  lifeInBusinessTitle: 'Life in our business',
  testimonial: '"Working here has given me the autonomy to lead major projects while receiving incredible mentorship. It’s a truly collaborative environment, and I have learnt so much from my colleagues.”',
  lifeInBusinessParagraphs: [
    'Our headquarters are based in ‘A’ Grade offices in Wolverhampton UK. We support those who require a flexible working arrangement, which can be discussed at your interview.',
    'We regularly have social events throughout the year where you can relax and have fun with many of your colleagues.'
  ]
};

const CAREER_JOBS = [
  {
    id: 'graduate-business-analyst',
    company: 'Lloyd James Media Group',
    companies: ['Lloyd James Media Group'],
    title: 'Business Analyst — Graduate Programme',
    vacancies: 1,
    meta: 'Wolverhampton | Full-Time, Permanent | Graduate Entry Level | £28,000 Starting Salary | July 2026',
    eyebrow: 'GRADUATE OPPORTUNITY',
    heading: 'Business Analyst — Graduate Programme',
    sections: [
      {
        title: 'Job snapshot',
        items: [
          'Location: Wolverhampton',
          'Full-Time, Permanent',
          'Graduate Entry Level',
          '£28,000 Starting Salary Competitive + benefits',
          'Career Level: Graduate / Entry Level',
          'Start Date: July 2026',
          'Programme: 2-year structured development scheme'
        ]
      },
      {
        title: 'Shape the Future with Data',
        paragraphs: [
          "Are you passionate about turning numbers into narratives and data into decisions? We are a data-driven organisation where insight isn't just a by-product — it's the engine behind everything we do. From market strategy to operational performance, our decisions are shaped by rigorous analysis, and we need sharp, curious minds to help us go further.",
          "This is more than an entry-level role. As a Graduate Business Analyst, you'll work at the heart of our business — collaborating with senior stakeholders, uncovering trends that matter, and influencing strategy from day one."
        ]
      },
      {
        title: 'About us',
        paragraphs: [
          'Lloyd James Media Group comprises of several inter-related businesses operating in a number of sectors including, Legal, IT, Media, Property and Travel. We are an innovative and award-winning company that recognises our people are crucial to our on-going success.',
          'We thrive in a fast-paced, commercially driven environment where data informs our direction. We have been around for over two decades, and we are expanding our business interests here in the UK and growing our customer base further in the EU. It’s an exciting time to join our friendly, professional and welcoming team of people.'
        ]
      },
      {
        title: 'About the Role',
        paragraphs: [
          "You'll join a collaborative and expanding analytics team that sits at the crossroads of data, strategy, and business performance. From your very first week, you'll be involved in real projects with real impact. Our structured two-year graduate development programme is designed to build your skills rapidly, expose you to different areas of the business, and develop you into a well-rounded analyst and future leader."
        ]
      },
      {
        title: "What you'll be doing",
        items: [
          'Analyse large, complex datasets to identify trends, opportunities, and risks that inform strategic decision-making',
          'Build and maintain interactive dashboards and reports that give stakeholders a real-time view of business performance',
          'Work alongside senior analysts and business leaders to translate data findings into clear, compelling recommendations',
          'Support the design and evaluation of business improvement initiatives using data-backed evidence',
          'Contribute to forecasting, scenario modelling, and KPI tracking across business functions',
          'Present insights to both technical and non-technical audiences, turning complex outputs into engaging stories',
          'Collaborate with Finance, Operations, Marketing, and Product teams',
          'Continuously explore new data sources, tools, and methodologies to sharpen our analytical edge'
        ]
      },
      {
        title: "What We're Looking For",
        paragraphs: [
          "We're looking for analytically-minded graduates who are as comfortable with a spreadsheet as they are presenting to a boardroom. You’ll likely to have studied Business Analytics, Data Science, Economics, Mathematics, Computer Science, or a related degree — but what matters most is your curiosity, your drive, and your ability to think critically."
        ]
      },
      {
        title: 'Essential skills & experience',
        items: [
          'A degree (2:1 or above) in Business Analytics, Data Science, Economics, Statistics, Computer Science, or a numerate/analytical discipline',
          'Proficiency in data analysis tools — Excel (advanced), SQL, Python, R, or similar',
          'Experience working with data visualisation platforms such as Power BI, Tableau, MongoDB or Looker',
          'Strong analytical and problem-solving skills with a logical, evidence-based approach',
          'Excellent written and verbal communication skills — able to convey complex ideas simply and persuasively',
          'Comfortable working with ambiguity and structuring your own approach to open-ended problems',
          'Genuine interest in business operations and how data can drive better outcomes'
        ]
      },
      {
        title: 'Nice to have',
        items: [
          'Experience with statistical modelling, regression analysis, or A/B testing',
          'Exposure to machine learning concepts or predictive analytics',
          'Familiarity with cloud-based data platforms (e.g. Google BigQuery, AWS, Azure)',
          'A previous Internship, placement year, or project work involving real business data'
        ]
      },
      {
        title: "Skills You'll Develop",
        paragraphs: [
          "We’ll invest seriously in your growth. During your time here, you'll build expertise across the full analytics lifecycle — from data collection and cleansing, through to advanced modelling and strategic communication. Here's a snapshot of what you'll gain:"
        ],
        items: [
          'Advanced SQL & Python',
          'Data Visualisation',
          'Statistical Analysis',
          'Business Intelligence',
          'Stakeholder Management',
          'Strategic Communication',
          'Process improvement and commercial acumen',
          'Predictive Modelling',
          'Agile Methodologies',
          'Storytelling with Data',
          'AI Command Skills'
        ]
      },
      {
        title: 'Your Career Journey',
        paragraphs: [
          "This role is the launchpad for a high-growth career in data and analytics. We're proud of our track record of promoting from within, and our graduate alumni have gone on to roles including Senior Analyst, Data Science Lead, Head of Insight, and Strategy Director. Here's how your journey could look:"
        ],
        table: {
          columns: ['Stage', 'Role', 'Timeline'],
          rows: [
            ['Entry', 'Graduate Business Analyst', 'Year 1–2'],
            ['Growth', 'Business Analyst / Data Analyst', 'Year 2–4'],
            ['Senior', 'Senior Analyst / Insights Manager', 'Year 4–6'],
            ['Leadership', 'Head of Analytics / Strategy Lead', 'Year 6+']
          ]
        }
      },
      {
        title: 'What We Offer',
        items: [
          'Competitive starting salary with an annual performance review',
          'Structured two-year graduate development programme with project rotations across business functions',
          'Dedicated mentoring from senior analysts and business leaders',
          'Access to industry-leading data tools, platforms, and certifications',
          'Flexible working arrangements',
          '20 days annual leave plus bank holidays',
          'Pension scheme with employer contributions',
          'Regular team socials and innovation challenges',
          'Clear pathways for rapid promotion based on performance, not tenure'
        ]
      },
      {
        title: 'Why Join Us?',
        paragraphs: [
          "We don't just collect data — we live by it. Every strategic decision we make is underpinned by rigorous analysis, and our analysts are respected, listened to, and empowered to challenge thinking at every level. You won't be buried in a back-office team. You'll be working alongside senior leaders, presenting to the board, and seeing the tangible impact of your work on the direction of the company.",
          "If you want a career where data is genuinely valued, where your ideas will be heard, and where you'll grow faster than you thought possible — this is the place for you."
        ]
      }
    ],
    ctaHeading: 'Ready to begin your analytics career?',
    ctaText: "Apply now and tell us how you've used data to solve a problem — big or small."
  },
  {
    id: 'business-intelligence-performance-analyst',
    company: 'Air Travel Claim',
    companies: ['Air Travel Claim'],
    title: 'Business Intelligence & Performance Analyst',
    vacancies: 1,
    meta: 'Wolverhampton | Full-Time | Permanent | £35,000–£40,000 per annum (DOE)',
    heading: 'Business Intelligence & Performance Analyst',
    sections: [
      {
        title: 'Shape Your future with Us',
        paragraphs: [
          'We are a growing, technology-led consumer claims group operating across regulated and non-regulated claims environments. Our businesses specialise in managing high-volume consumer claims processes with a strong focus on operational excellence, compliance, and customer outcomes.',
          'We are investing heavily in automation, data intelligence, and AI-supported workflows to create scalable, efficient, and high-performing claims operations. This role sits at the heart of that operational growth.'
        ]
      },
      {
        title: 'Your Role',
        paragraphs: [
          'We are looking for a Business Intelligence & Performance Analyst who goes beyond building reports. This role requires someone who can interpret operational data, identify issues before they escalate, and provide insight-driven recommendations that improve performance and efficiency across the organisation.',
          'You will manage and optimise data flows, maintain our MongoDB environments, and develop automation and reporting solutions. Crucially, you will also analyse what the data means, spot trends or performance gaps, and work with stakeholders to implement practical solutions.',
          'This is an ideal role for someone who enjoys improving business systems, solving problems, and turning raw data into meaningful action.'
        ]
      },
      {
        title: 'Key Responsibilities',
        items: [
          'Build dashboards and reports that provide clear insight not just data with commentary, context, and recommended actions.',
          'Analyse operational data to identify performance issues, inefficiencies, and risks, and proactively present findings to management.',
          'Diagnose the root cause of data, system, or process problems and recommend practical, data-driven solutions.',
          'Maintain and optimise MongoDB databases, including indexing, data modelling, and query tuning to ensure strong performance.',
          'Support migrations between SQL and NoSQL systems, ensuring data accuracy, consistency, and security.',
          'Develop automation, alerts, and monitoring that help the business work more efficiently and reduce manual tasks.',
          'Collaborate with both technical and operational teams to ensure data is used effectively to support decision-making and growth.'
        ]
      },
      {
        title: 'Skills & Experience',
        items: [
          '2–4 years’ experience in data analytics, database management, or operational support.',
          'Strong proficiency in MongoDB administration, including performance optimisation.',
          'Working knowledge of SQL and experience with SQL–NoSQL migrations.',
          'Strong analytical skills: ability to interpret data, diagnose problems, and make clear recommendations.',
          'Excellent communication, solution-focused mindset and organisational skills.'
        ]
      },
      {
        title: 'Nice to have',
        items: [
          'You are someone who thinks critically, not just technically.',
          'Someone who gets curious when they see an unexpected trend and digs into why. You proactively communicate issues and bring suggestions for improvement.',
          'Someone who can explain technical concepts in a clear, simple way to non-technical stakeholders.',
          'Someone who enjoys problem solving as much as building dashboards or writing queries.'
        ]
      },
      {
        title: 'Benefits',
        items: [
          'Competitive salary',
          '20 days annual leave + bank holidays',
          'Hands-on experience in a tech-enabled, data-driven operations team',
          'Exposure to automation, AI-supported workflows, and modern systems',
          'Ongoing training and development',
          'Clear progression into senior operations roles'
        ]
      },
      {
        title: 'Why Join Us?',
        paragraphs: [
          'Career Growth: You will grow your career while being part of a company that is pioneering AI-driven innovation and automation across multiple industries.',
          "Supportive Culture: You'll be part of a collaborative, high-performance team where knowledge-sharing and mentoring are part of our DNA. We offer a fun, inclusive, and fast-paced environment where your ideas are not only heard they’re encouraged.",
          'Modern Practice: Work with advanced AI-powered tools, streamlined workflows, and full administrative support.',
          'Strategic Expansion: With ambitious growth plans and a national footprint, we offer exciting long-term opportunities in a dynamic firm.',
          'Equality, Diversity & Inclusion: We are an equal opportunities employer and are committed to creating an inclusive and supportive working environment. Applications are welcomed from all suitably qualified individuals.'
        ]
      }
    ],
    ctaHeading: 'Ready to Redefine Your Career?',
    ctaText: 'If you can turn data into insight, and insight into measurable business performance we’d love to hear from you! Share your CV with us!',
    note: '*This job specification may be reviewed and updated periodically to reflect the evolving needs of the business.'
  },
  {
    id: 'litigation-paralegal-flight-delay',
    company: 'Air Travel Claim',
    companies: ['Air Travel Claim'],
    title: 'Litigation Paralegal – Flight Delay Claims (EC261/UK261)',
    vacancies: 1,
    meta: 'Wolverhampton | Full-Time | Permanent | £27,000 – £30,000 (Depends on Experience)',
    heading: 'Litigation Paralegal – Flight Delay Claims (EC261/UK261)',
    sections: [
      {
        title: 'Shape Your future with Us',
        paragraphs: [
          'We are a growing, technology-led consumer claims group operating across regulated and non-regulated claims environments. Our businesses specialise in managing high-volume consumer claims processes with a strong focus on operational excellence, compliance, and customer outcomes.',
          'As a leading firm specialising in consumer rights and air travel disruption claims our mission is to advocate for passengers and help them seek justice against airlines, ensuring fair treatment for all travellers. Our team is passionate, dedicated, and driven by a shared purpose to make a difference in the lives of many.'
        ]
      },
      {
        title: 'Your Role',
        paragraphs: [
          'We are currently looking for a Litigation Paralegal – Flight Delay Claims (EC261/UK261) to join our dynamic team in Wolverhampton, a hub for legal excellence in consumer rights and air travel disruption claims. This is your chance to be part of a dedicated group making a tangible impact on passengers’ lives while advancing your legal career.'
        ]
      },
      {
        title: 'Key Responsibilities',
        paragraphs: [
          'As a Litigation Paralegal – Flight Delay Claims (EC261/UK261), you will support our legal team by handling a high volume of small claims relating to flight delays, cancellations and denied boarding. Your responsibilities will include preparing cases for litigation, liaising effectively with clients, airlines, and courts, and ensuring all case procedures are managed efficiently and within deadlines.'
        ]
      },
      {
        title: 'Skills & Experience',
        items: [
          'Minimum 1 year of experience in litigation or civil disputes, preferably within consumer or personal injury sectors',
          'Strong understanding of the Small Claims Track and CPR protocols',
          'Experience or knowledge of EC261/UK261 or air travel litigation (desirable but not essential)',
          'Exceptional attention to detail and excellent case management skills',
          'Outstanding written and verbal communication abilities'
        ]
      },
      {
        title: 'Benefits',
        items: [
          'Competitive salary based on experience',
          '20 days annual leave + bank holidays',
          'Hands-on experience in a tech-enabled, data-driven operations team',
          'Exposure to automation, AI-supported workflows, and modern systems',
          'Ongoing training and development',
          'Clear progression into senior operations roles'
        ]
      },
      {
        title: 'Why Join Us?',
        paragraphs: [
          'Career Growth: You will grow your career while being part of a company that is pioneering AI-driven innovation and automation across multiple industries.',
          "Supportive Culture: You'll be part of a collaborative, high-performance team where knowledge-sharing and mentoring are part of our DNA. We offer a fun, inclusive, and fast-paced environment where your ideas are not only heard they’re encouraged.",
          'Modern Practice: Work with advanced AI-powered tools, streamlined workflows, and full administrative support.',
          'Strategic Expansion: With ambitious growth plans and a national footprint, we offer exciting long-term opportunities in a dynamic firm.',
          'Equality, Diversity & Inclusion: We are an equal opportunities employer and are committed to creating an inclusive and supportive working environment. Applications are welcomed from all suitably qualified individuals.'
        ]
      }
    ],
    ctaHeading: 'Ready to Redefine Your Career?',
    ctaText: 'If you believe you are a strong fit for this role, we would love to hear from you. Apply today to join our team and take the next step in your legal career, supporting passengers and making a real impact in airline consumer rights.',
    note: '*This job specification may be reviewed and updated periodically to reflect the evolving needs of the business.'
  },
  {
    id: 'senior-mongodb-engineer',
    company: 'Tanan Tech Limited',
    companies: ['Tanan Tech Limited'],
    title: 'Senior MongoDB Engineer',
    vacancies: 1,
    meta: 'Wolverhampton | Full-time | Permanent | £45,000 - £65,000 per annum | Depending on experience/ Sponsorship requirements',
    heading: 'Senior MongoDB Engineer',
    sections: [
      {
        title: 'Shape Your Future with Us:',
        paragraphs: [
          'We provide specialised software development and technical support, and we excel in delivering tailored solutions and comprehensive support services. We have successfully delivered custom software development, system integration, and ongoing technical support, enabling the group to enhance its operational efficiency and scalability. Our unique ability to bridge technical intricacies with business needs positions us as a key driver of group’s technological strategy.'
        ]
      },
      {
        title: 'Your Role',
        paragraphs: [
          'We are building a scalable Car Claims platform involving OCR ingestion, multi-stage workflows, and external integrations. We are looking for a hands-on MongoDB expert with deep experience in database design, performance tuning, and cluster management within AWS cloud environments.',
          'This role requires someone who has worked extensively on production-grade MongoDB clusters, ensuring high availability, performance, and resilience in distributed systems.'
        ]
      },
      {
        title: 'Key Responsibilities',
        subsections: [
          {
            title: 'MongoDB Architecture & Engineering',
            items: [
              'Design and implement scalable MongoDB data models (schema design) for:',
              'Build and manage MongoDB clusters (replica sets & sharded clusters)'
            ]
          },
          {
            title: 'Performance Tuning & Optimization',
            items: [
              'Perform advanced query optimization and indexing strategies',
              'Proactively identify and resolve performance bottlenecks'
            ]
          },
          {
            title: 'Cluster Management & High Availability',
            items: [
              'Configure and manage:',
              'Replica sets (primary, secondary)',
              'Sharding (chunk splitting, balancing, migrations)',
              'Handle failover, replication lag, and cluster health monitoring'
            ]
          },
          {
            title: 'Backup, Recovery & Data Migration',
            items: [
              'Implement backup and recovery strategies (point-in-time recovery where applicable)',
              'Ensure data integrity during schema evolution and migrations'
            ]
          }
        ]
      },
      {
        title: 'Required Skills & Experience',
        items: [
          '8 - 10 years of experience in Database Engineering',
          'Strong hands-on expertise in MongoDB (core requirement)',
          'Proven experience in: Sharding & replication, Cluster management',
          'Deep knowledge of: Performance tuning, Query optimization, Indexing strategies',
          'Experience with: Backup & recovery, Data migration, Troubleshooting production issues',
          'Hands-on AWS cloud platform experience',
          'Strong understanding of NoSQL data modelling'
        ]
      },
      {
        title: 'Nice to have',
        items: [
          'Experience with: MySQL / SQL Server / Cassandra (multi-database exposure)'
        ]
      },
      {
        title: 'Benefits',
        items: [
          'Competitive salary',
          '20 days annual leave + bank holidays',
          'Hands-on experience in a tech-enabled, data-driven operations team',
          'Exposure to automation, AI-supported workflows, and modern systems',
          'Ongoing training and development',
          'Clear progression into senior operations roles'
        ]
      },
      {
        title: 'Why Join Us?',
        paragraphs: [
          'Career Growth: You will grow your career while being part of a company that is pioneering AI-driven innovation and automation across multiple industries.',
          "Supportive Culture: You'll be part of a collaborative, high-performance team where knowledge-sharing and mentoring are part of our DNA. We offer a fun, inclusive, and fast-paced environment where your ideas are not only heard they’re encouraged.",
          'Modern Practice: Work with advanced AI-powered tools, streamlined workflows, and full administrative support.',
          'Strategic Expansion: With ambitious growth plans and a national footprint, we offer exciting long-term opportunities in a dynamic firm.',
          'Equality, Diversity & Inclusion: We are an equal opportunities employer and are committed to creating an inclusive and supportive working environment. Applications are welcomed from all suitably qualified individuals.'
        ]
      }
    ],
    ctaHeading: 'Ready to Redefine Your Career?',
    ctaText: 'If you are looking to grow your career and be part of a fast-moving, technology-led business, we would love to hear from you. Share your CV with us!',
    note: '*This job specification may be reviewed and updated periodically to reflect the evolving needs of the business.'
  },
  {
    id: 'operations-business-administrator',
    company: 'Your Claim Matters & Air Travel Claim',
    companies: ['Your Claim Matters', 'Air Travel Claim'],
    title: 'Operations Business Administrator',
    vacancies: 5,
    meta: 'Wolverhampton | Full-Time | Permanent | £26,000 – £30,000 (DOE)',
    heading: 'Operations Business Administrator',
    sections: [
      {
        title: 'Shape Your future with Us',
        paragraphs: [
          'We are a growing, technology-led consumer claims group operating across regulated and non-regulated claims environments. Our businesses specialise in managing high-volume consumer claims processes with a strong focus on operational excellence, compliance, and customer outcomes.',
          'We are investing heavily in automation, data intelligence, and AI-supported workflows to create scalable, efficient, and high-performing claims operations. This role sits at the heart of that operational growth.'
        ]
      },
      {
        title: 'Your Role',
        paragraphs: [
          'We are looking for people who want to make a difference, people who take ownership, stay curious, and help move our business forward.',
          'As part of our Operations team, you will support the day-to-day handling of consumer claims and play a key role in understanding processes, supporting operational excellence, and contributing to a fast-moving environment powered by technology and intelligent processes.',
          'We are a technology-led business with cutting-edge systems, automation, and AI-assisted workflows. We welcome people who enjoy working in a modern, digital-first environment and who feel energised by using technology to solve problems, streamline processes, and drive continual improvement.'
        ]
      },
      {
        title: 'Key Responsibilities',
        subsections: [
          {
            title: 'Operational Support',
            items: [
              'Review and process high-volume consumer claims in line with internal processes and quality standards',
              'Maintain and update case management systems and workflow tools',
              'Track case progress and ensure timely movement through each stage',
              'Identify missing information and follow up with relevant parties',
              'Support quality control checks to ensure accuracy and compliance'
            ]
          },
          {
            title: 'Workflow & Performance Support',
            items: [
              'Monitor workflow queues and flag delays, risks, or bottlenecks',
              'Support SLA and KPI tracking through accurate data input and reporting',
              'Assist with operational reporting, data tracking, and insight gathering'
            ]
          },
          {
            title: 'Process Improvement & Continuous Improvement',
            items: [
              'Support implementation of process improvements across the claims lifecycle',
              'Use systems, data, and tools to help improve performance and efficiency',
              'Build an understanding of operational processes and identify patterns, issues, or improvement opportunities',
              'Contribute ideas for automation, system improvements, and smarter ways of working'
            ]
          },
          {
            title: 'Stakeholder & Business Support',
            items: [
              'Communicate clearly with internal teams and external partners to progress cases',
              'Work collaboratively across teams to support operational delivery',
              'Ensure adherence to internal processes and regulatory requirements'
            ]
          }
        ]
      },
      {
        title: 'Skills & Experience',
        items: [
          'Has experience or a strong interest in operations, claims, legal, financial services, or a similar processing environment',
          'Is comfortable working with systems, CRM platforms, or workflow tools',
          'Is highly organised with strong attention to detail',
          'Is reliable and takes ownership of their work',
          'Is a clear communicator who can engage with colleagues and stakeholders',
          'Is curious, proactive, and eager to learn',
          'Is comfortable managing multiple tasks and prioritising workload'
        ]
      },
      {
        title: 'Nice to have:',
        items: [
          'Experience in admin, operations, or customer support roles',
          'Familiarity with Excel or Google Sheets',
          'Interest in process improvement or data-led operations'
        ]
      },
      {
        title: 'Benefits',
        items: [
          'Competitive salary',
          '20 days annual leave + bank holidays',
          'Hands-on experience in a tech-enabled, data-driven operations team',
          'Exposure to automation, AI-supported workflows, and modern systems',
          'Ongoing training and development',
          'Clear progression into senior operations roles'
        ]
      },
      {
        title: 'Why Join Us?',
        paragraphs: [
          'Career Growth: You will grow your career while being part of a company that is pioneering AI-driven innovation and automation across multiple industries.',
          "Supportive Culture: You'll be part of a collaborative, high-performance team where knowledge-sharing and mentoring are part of our DNA. We offer a fun, inclusive, and fast-paced environment where your ideas are not only heard they’re encouraged.",
          'Modern Practice: Work with advanced AI-powered tools, streamlined workflows, and full administrative support.',
          'Strategic Expansion: With ambitious growth plans and a national footprint, we offer exciting long-term opportunities in a dynamic firm.',
          'Equality, Diversity & Inclusion: We are an equal opportunities employer and are committed to creating an inclusive and supportive working environment. Applications are welcomed from all suitably qualified individuals.'
        ]
      }
    ],
ctaHeading: 'Ready to Redefine Your Career?',
    ctaText: 'If you are looking to start or grow your career in operations and want to be part of a fast-moving, technology-led business, we would love to hear from you. Share your CV with us!',
    note: '*This job specification may be reviewed and updated periodically to reflect the evolving needs of the business.'
  },
  {
    id: 'ai-prompt-engineer',
    company: 'Tanan Tech Limited',
    companies: ['Tanan Tech Limited'],
    title: 'AI Prompt Engineer',
    vacancies: 1,
    meta: 'Wolverhampton | Full-Time | Permanent | £30,000 - £35,000 for a post-grad/ level entry per annum (DOE)',
    heading: 'AI Prompt Engineer',
    sections: [
      {
        title: 'Shape Your future with Us',
        paragraphs: [
          'We provide specialised software development and technical support, and we excel in delivering tailored solutions and comprehensive support services. We have successfully delivered custom software development, system integration, and ongoing technical support, enabling the group to enhance its operational efficiency and scalability. Our unique ability to bridge technical intricacies with business needs positions us as a key driver of group’s technological strategy.'
        ]
      },
      {
        title: 'Your Role',
        paragraphs: [
          'As part of our Operations and Technology function, we are looking for AI Prompt Engineer who will work closely with our AI & Automations Manager, playing a key role in designing, building, and maintaining AI-powered workflows, prompt systems, and intelligent automations that support the wider business.',
          'This role blends technical execution with operational understanding. You will be involved in AI prompt engineering, automation logic, and system integration, while also helping the business understand processes, improve efficiency, and scale intelligent solutions.'
        ]
      },
      {
        title: 'Key Responsibilities',
        items: [
          'Support our AI & Automations Manager in designing, building, testing, and maintaining AI-driven solutions',
          'Engineer, refine, and maintain AI prompts, prompt frameworks, and reusable prompt patterns',
          'Build and support automations, workflows, and integrations across business systems',
          'Write, review, and maintain code used in AI pipelines, automation logic, data handling, or system integrations',
          'Translate business and operational requirements into effective AI and automation solutions',
          'Explore, recommend, and implement process and AI performance improvement',
          'Support data collection, analysis, and insight generation to improve AI outputs and workflows',
          'Document prompts, workflows, decisions, and system logic clearly',
          'Communicate progress, risks, and opportunities effectively across the business'
        ]
      },
      {
        title: 'Skills & Experience',
        items: [
          'Tech-confident – you are comfortable with coding, AI models, prompt engineering, automation tools, APIs, and modern software systems'
        ],
        subsections: [
          {
            title: 'Technical Capability (Required), strong knowledge and hands-on experience in:',
            items: [
              'AI prompt engineering and prompt optimisation',
              'Working with large language models (LLMs) and AI-assisted workflows',
              'Coding (e.g. Python, JavaScript, or similar)',
              'Automation platforms, APIs, and system integrations',
              'Logical problem-solving, debugging, and structured thinking',
              'Working with data, outputs, and performance feedback loops'
            ]
          }
        ]
      },
      {
        title: 'Nice to have',
        items: [
          'You take responsibility and follow through',
          'You take pride in building AI solutions, prompts, and automations that you can stand behind',
          'You challenge assumptions whether in processes, logic, or prompt design when something doesn’t look right',
          'You are curious, analytical, and eager to learn',
          'You actively seek better ways to improve AI performance, reliability, and usefulness',
          'You help us move forward as a team',
          'You communicate clearly with technical and non-technical stakeholders.',
          'You support the development of intelligent, efficient processes that elevate overall performance'
        ]
      },
      {
        title: 'Benefits',
        items: [
          'Competitive salary',
          '20 days annual leave + bank holidays',
          'Hands-on experience in a tech-enabled, data-driven operations team',
          'Exposure to automation, AI-supported workflows, and modern systems',
          'Ongoing training and development',
          'Clear progression into senior operations roles'
        ]
      },
      {
        title: 'Why Join Us?',
        paragraphs: [
          'Career Growth: You will grow your career while being part of a company that is pioneering AI-driven innovation and automation across multiple industries.',
          "Supportive Culture: You'll be part of a collaborative, high-performance team where knowledge-sharing and mentoring are part of our DNA. We offer a fun, inclusive, and fast-paced environment where your ideas are not only heard they’re encouraged.",
          'Modern Practice: Work with advanced AI-powered tools, streamlined workflows, and full administrative support.',
          'Strategic Expansion: With ambitious growth plans and a national footprint, we offer exciting long-term opportunities in a dynamic firm.',
          'Equality, Diversity & Inclusion: We are an equal opportunities employer and are committed to creating an inclusive and supportive working environment. Applications are welcomed from all suitably qualified individuals.'
        ]
      }
    ],
    ctaHeading: 'Ready to Redefine Your Career?',
    ctaText: "If you’re passionate about shaping AI behavior one prompt at a time, we’d love to hear from you! Share your CV with us!",
    note: '*This job specification may be reviewed and updated periodically to reflect the evolving needs of the business.'
  },
  {
    id: 'head-of-engineering-and-operations',
    company: 'Lloyd James Media Group',
    companies: ['Lloyd James Media Group'],
    title: 'Head of Engineering & Operations',
    meta: 'Technology & IT | Full-Time | Permanent',
    eyebrow: 'TECHNOLOGY & IT',
    heading: 'Head of Engineering & Operations',
    sections: [
      {
        title: 'Shape the technology behind our growth',
        paragraphs: [
          'We are a growing, technology-led consumer claims group operating across regulated and non-regulated claims environments. Our businesses manage high-volume consumer claims processes, with a strong focus on operational excellence, compliance, customer outcomes and scalable technology.',
          'We are investing in automation, data intelligence, AI-supported workflows and purpose-built systems to increase capacity, improve control and create better operational outcomes. We are looking for a senior technology leader who can ensure our engineering capability and technical operations scale with that ambition.'
        ]
      },
      {
        title: 'The Role',
        paragraphs: [
          'The Head of Engineering & Operations will lead the technology function at the intersection of software engineering, technical operations and the wider business. You will be accountable for reliable delivery of technology change, the performance and resilience of the systems that support day-to-day operations, and the effectiveness of the engineering team.',
          'This is a hands-on leadership role for someone who can move comfortably between strategy and delivery. You will work closely with operational leaders and product owners to understand business problems, translate them into clear technical priorities and ensure solutions are delivered safely, pragmatically and at pace.',
          'The role requires strong technical judgement, people leadership and commercial awareness. You do not need to be the person writing every line of code, but you must be technically credible enough to challenge architecture and engineering decisions, understand delivery risk and create a high-performing engineering environment.'
        ]
      },
      {
        title: 'Key Responsibilities',
        subsections: [
          {
            title: 'Engineering Leadership & Delivery',
            items: [
              'Lead and develop the software engineering function, creating clear accountability, delivery standards and a high-performance culture.',
              'Own engineering delivery across business-critical platforms, integrations, automation and internal systems.',
              'Work with Product and Operations leaders to turn business requirements into well-defined, prioritised and achievable technical work.',
              'Improve engineering planning, estimation, delivery cadence and visibility so stakeholders understand priorities, dependencies, risks and progress.',
              'Provide technical leadership on architecture, build-versus-buy decisions, integrations, scalability and technical debt.',
              'Ensure appropriate development, testing, release and change-control practices are embedded without creating unnecessary bureaucracy.'
            ]
          },
          {
            title: 'Technology Operations & Reliability',
            items: [
              'Take ownership of the operational health, availability, resilience and performance of business-critical technology services.',
              'Establish effective incident, problem and change management processes, including clear escalation and post-incident learning.',
              'Ensure monitoring, alerting, support arrangements and operational documentation are appropriate for a high-volume processing business.',
              'Identify recurring system or workflow failures and drive permanent fixes rather than relying on manual workarounds.',
              'Oversee third-party technology suppliers and services, ensuring performance, value and accountability.'
            ]
          },
          {
            title: 'Operational Technology & Automation',
            items: [
              'Partner closely with Operations to understand end-to-end claims workflows, bottlenecks, failure points and opportunities for automation.',
              'Help design technology that supports efficient high-volume processing, strong controls, accurate data and effective exception handling.',
              'Champion pragmatic automation and AI-supported workflows where they create measurable operational value.',
              'Use operational and technical data to identify opportunities to improve throughput, quality, reliability and cost-to-serve.',
              'Ensure new technology is implemented successfully into BAU operations, with appropriate testing, user acceptance, rollout and measurement.'
            ]
          },
          {
            title: 'Strategy, Governance & Risk',
            items: [
              'Develop and maintain a technology roadmap aligned to business growth, product priorities and operational needs.',
              'Provide senior leadership with clear information on technology performance, delivery, risks, investment requirements and trade-offs.',
              'Ensure appropriate controls exist around access, data, security, change, resilience and business continuity, working with relevant internal and external specialists where required.',
              'Balance speed of delivery with maintainability, resilience, security and the needs of a regulated/high-control operating environment.',
              'Own or contribute to technology budgets, supplier decisions and resource planning.'
            ]
          },
          {
            title: 'People & Cross-Functional Leadership',
            items: [
              'Coach, develop and performance-manage engineering and technical team members, building capability as the business scales.',
              'Create strong working relationships between Engineering, Product, Operations and senior leadership.',
              'Build a culture where engineers understand the operational and customer impact of the systems they create.',
              'Act as the senior escalation point for complex technical delivery and production issues.'
            ]
          }
        ]
      },
      {
        title: 'What We Are Looking For',
        paragraphs: [
          'We are looking for someone who can combine technical depth with operational and commercial judgement. You are likely to have:'
        ],
        items: [
          'Significant experience leading software engineering, technology delivery or a closely related technical function.',
          'Experience managing and developing engineers and creating effective delivery practices.',
          'Strong technical understanding of modern software systems, APIs/integrations, databases, cloud or hosted environments, and software delivery lifecycles.',
          'Experience owning or supporting production systems where availability, reliability and operational continuity matter.',
          'A track record of delivering technology change that improves real business processes and measurable operational outcomes.',
          'Experience working with non-technical stakeholders and translating business problems into clear technical requirements and priorities.',
          'Strong project and programme delivery capability, including managing dependencies, risk, competing priorities and stakeholder expectations.',
          'A data-led, problem-solving mindset with the ability to diagnose root causes rather than simply treat symptoms.',
          'Commercial judgement and the ability to make sensible trade-offs between speed, cost, technical quality and business value.',
          'The confidence to operate as a senior leader while remaining close enough to delivery to understand what is really happening.'
        ]
      },
      {
        title: 'Highly Desirable',
        items: [
          'Experience in claims management, financial services, legal services, insurance, lending, complaints/redress or another high-volume processing environment.',
          'Experience delivering workflow automation, CRM/case-management platforms, data-led operational tools or AI-enabled processes.',
          'Experience operating technology within a regulated or strongly controlled environment.',
          'Experience scaling an engineering or technology function during a period of rapid business growth.'
        ]
      },
      {
        title: 'How Success Will Be Measured',
        items: [
          'Predictable, transparent delivery of high-value technology priorities.',
          'Improved reliability and resilience of business-critical systems.',
          'Reduction in recurring incidents, manual workarounds and technology-driven operational bottlenecks.',
          'Measurable improvements in operational efficiency enabled by technology and automation.',
          'A stronger, accountable and increasingly capable engineering team.',
          'Improved partnership and clarity between Engineering, Product and Operations.'
        ]
      },
      {
        title: 'Why Join Us?',
        items: [
          'Influence: Own and shape a technology function that is central to the way the group operates and scales.',
          'Technology-led growth: Work in a business actively investing in automation, data intelligence, AI-supported workflows and modern systems.',
          'Visible impact: See engineering decisions translate directly into operational performance, capacity and customer outcomes.',
          'Leadership opportunity: Build strong engineering and technical operations disciplines while partnering directly with senior business leaders.',
          'Supportive culture: Join a collaborative, fast-moving environment where practical ideas and continuous improvement are encouraged.'
        ]
      },
      {
        title: 'Equality, Diversity & Inclusion',
        paragraphs: [
          'We are an equal opportunities employer and are committed to creating an inclusive and supportive working environment. Applications are welcomed from all suitably qualified individuals.'
        ]
      }
    ],
    ctaText: 'If you are a technology leader who enjoys building strong teams, solving operational problems and turning engineering capability into measurable business outcomes, we would love to hear from you.',
    note: 'This job specification may be reviewed and updated periodically to reflect the evolving needs of the business.'
  },
  {
    id: 'head-of-operations',
    company: 'Lloyd James Media Group',
    companies: ['Lloyd James Media Group'],
    title: 'Head of Operations',
    meta: 'Wolverhampton | Service Operations | Full-Time | Permanent',
    eyebrow: 'SERVICE OPERATIONS',
    heading: 'Head of Operations',
    sections: [
      {
        title: 'Shape your future with us',
        paragraphs: [
          'We are a growing, technology-led consumer claims group operating across regulated and non-regulated claims environments. Our businesses manage high-volume consumer claims and service processes, with a strong focus on operational excellence, compliance, customer outcomes and scalable delivery.',
          'We are investing in automation, data intelligence and AI-supported workflows to build efficient, high-performing operations. The Head of Operations will play a central role in translating that investment into consistently strong service delivery, productive teams and better customer outcomes.'
        ]
      },
      {
        title: 'The Role',
        paragraphs: [
          'We are seeking an experienced Head of Operations to provide senior leadership across our service and processing operations. This is a broad operational leadership role for someone who can create structure, raise performance, develop managers and build an operation capable of scaling with the business.',
          'You will be accountable for day-to-day operational performance while also looking beyond today: improving capacity, productivity, quality, customer outcomes and the processes that underpin service delivery. You will work closely with senior leadership, Product, Technology, Compliance and other functions to turn business priorities into effective operational execution.',
          'This role would suit a commercially minded service-sector leader with experience managing sizeable, high-volume operations. Claims management, financial services, legal services or other regulated processing experience would be particularly valuable, but strong candidates from comparable service environments are encouraged.'
        ]
      },
      {
        title: 'Key Responsibilities',
        subsections: [
          {
            title: 'Operational Leadership & Service Delivery',
            items: [
              'Take senior ownership of day-to-day service and processing operations, ensuring teams deliver against agreed customer, quality and commercial outcomes.',
              'Set clear operating rhythms, priorities and accountabilities across operational teams and managers.',
              'Monitor volumes, workflow, service levels, backlogs, productivity and quality; intervene quickly where performance moves off plan.',
              'Ensure effective case/work allocation, escalation management and end-to-end process control.',
              'Build a culture of ownership, pace, consistency and high standards.'
            ]
          },
          {
            title: 'Performance, Capacity & MI',
            items: [
              'Own operational KPIs and performance reporting, ensuring measures are meaningful, understood and acted upon.',
              'Use data and management information to identify trends, constraints, root causes and opportunities.',
              'Lead workforce and capacity planning so resources align with forecast demand, service commitments and business priorities.',
              'Develop practical performance plans where teams or processes are underperforming and track delivery to outcome.',
              'Provide senior leadership with clear, concise insight into operational performance, risks and priorities.'
            ]
          },
          {
            title: 'People Leadership & Management',
            items: [
              'Lead, coach and develop operational managers and team leaders, creating a strong and accountable management layer.',
              'Set expectations for performance management, one-to-ones, coaching, development and succession planning.',
              'Support recruitment, onboarding and organisational design as the operation grows.',
              'Create an environment where teams understand the customer, the commercial objective and how their work contributes to both.',
              'Promote collaboration, continuous learning and constructive challenge.'
            ]
          },
          {
            title: 'Continuous Improvement & Change',
            items: [
              'Own and drive continuous improvement across service journeys and high-volume operational processes.',
              'Identify bottlenecks, unnecessary manual activity, failure demand and control weaknesses, then lead practical improvements.',
              'Work closely with Product and Technology teams to translate operational problems into clear requirements for systems, automation and workflow improvements.',
              'Sponsor and support operational change, including requirements definition, testing, implementation, training and post-launch adoption.',
              'Ensure changes deliver measurable improvements rather than simply introducing new processes or technology.'
            ]
          },
          {
            title: 'Customer, Quality & Compliance',
            items: [
              'Ensure customer outcomes and service quality remain central to operational decision-making.',
              'Maintain effective operational controls and ensure teams follow documented processes, policies and regulatory requirements.',
              'Work closely with Compliance, Quality and relevant stakeholders to address emerging risks, complaints, recurring errors and control issues.',
              'Embed root-cause thinking so recurring service or quality problems are addressed at source.'
            ]
          },
          {
            title: 'Strategy & Cross-Functional Leadership',
            items: [
              'Translate business strategy and growth plans into practical operational priorities, structures and delivery plans.',
              'Partner with senior leaders across Operations, Product, Technology, Compliance and commercial functions.',
              'Contribute to decisions on operating model, technology investment, automation, outsourcing and organisational design.',
              'Lead significant operational projects and change programmes where required.',
              'Act as a visible senior leader who can move comfortably between strategic discussion and operational detail.'
            ]
          }
        ]
      },
      {
        title: 'What We Are Looking For',
        items: [
          'Significant leadership experience in a high-volume service, processing or operational environment.',
          'A strong track record of leading through managers/team leaders rather than only directly supervising frontline teams.',
          'Evidence of improving operational performance across service levels, productivity, quality and customer outcomes.',
          'Strong experience with KPIs, MI, workforce/capacity planning and performance management.',
          'Experience leading operational change, process improvement or transformation initiatives.',
          'Commercially aware, data-driven and comfortable making decisions using both evidence and operational judgement.',
          'Confident working with technology, CRM/workflow platforms, automation and digitally enabled processes.',
          'Able to collaborate effectively with Product and Software/Technology teams without needing to be a technical specialist.',
          'Strong stakeholder management and communication skills, including the ability to influence senior colleagues.',
          'A pragmatic, hands-on leadership style with the ability to create structure and accountability in a fast-moving environment.'
        ]
      },
      {
        title: 'Highly Desirable',
        items: [
          'Experience within claims management, financial services, insurance, legal services, complaints/redress or another regulated environment.',
          'Experience managing large-scale case-processing or transaction-processing operations.',
          'Experience supporting technology-led transformation, automation or AI-enabled operational change.',
          'Experience scaling teams or operations through periods of significant growth.'
        ]
      },
      {
        title: 'What Success Looks Like',
        items: [
          'Operational performance is visible, predictable and actively managed.',
          'Customers receive consistent, timely and high-quality outcomes.',
          'Managers take ownership and resolve issues at the appropriate level.',
          'Capacity and resources are aligned to demand and business priorities.',
          'Operational problems are identified early and addressed at root cause.',
          'Product and Technology investment is translated into measurable operational improvement.',
          'The operation can scale without sacrificing quality, control or customer experience.'
        ]
      },
      {
        title: 'Why Join Us?',
        items: [
          'Career Impact: You will have the opportunity to shape how a growing group delivers its operations, developing the people, processes and operating model needed for the next stage of growth.',
          'Technology-Led Environment: Work alongside Product and Technology teams in a business investing in automation, data intelligence and AI-supported workflows.',
          'Leadership Opportunity: Build strong operational management capability and influence decisions across service delivery, transformation and growth.',
          'Supportive Culture: Join a collaborative, high-performance environment where ownership, ideas and continuous improvement are encouraged.',
          'Equality, Diversity & Inclusion: We are an equal opportunities employer and are committed to creating an inclusive and supportive working environment. Applications are welcomed from all suitably qualified individuals.'
        ]
      }
    ],
    ctaText: 'If you are an experienced operational leader who can combine strong service delivery with people leadership, data-driven performance management and continuous improvement, we would love to hear from you. Please apply with your CV.',
    note: 'This job specification may be reviewed and updated periodically to reflect the evolving needs of the business.'
  },
  {
    id: 'head-of-performance-marketing',
    company: 'Lloyd James Media Group',
    companies: ['Lloyd James Media Group'],
    title: 'Head of Performance Marketing',
    meta: 'Wolverhampton | Full-Time | Permanent | Growth & Performance Marketing',
    eyebrow: 'GROWTH & PERFORMANCE MARKETING',
    heading: 'Head of Performance Marketing',
    sections: [
      {
        title: 'Shape your future with us',
        paragraphs: [
          'We are a growing, technology-led consumer claims group operating across regulated and non-regulated claims environments. Our businesses manage high-volume consumer claims processes, supported by modern software, automation, data and AI-enabled workflows.',
          'As we continue to scale, we are looking for a commercially driven Head of Performance Marketing to lead customer and claim acquisition across our portfolio, build a rigorous performance marketing function and ensure marketing investment translates into profitable, high-quality business growth.'
        ]
      },
      {
        title: 'The Role',
        paragraphs: [
          'This is a senior, commercially accountable role with ownership of performance marketing strategy, acquisition economics and channel performance. You will be responsible for turning marketing investment into qualified customers and claims, while understanding the full journey from first click through eligibility, conversion, operational progression and ultimately commercial value.',
          'You will lead paid acquisition, measurement, conversion optimisation, forecasting and experimentation, working closely with Operations, Product, Technology, Finance and Compliance. Success will not be judged on traffic or lead volume alone: you will be expected to understand lead quality, downstream conversion, cost to acquire and the return generated by each channel, campaign and customer cohort.'
        ]
      },
      {
        title: 'Responsibilities',
        subsections: [
          {
            title: 'Performance Marketing Strategy & Commercial Ownership',
            items: [
              'Own and develop the performance marketing strategy across the group, aligned to growth targets, operational capacity and commercial objectives.',
              'Take accountability for acquisition performance and marketing contribution, balancing volume, quality, cost and downstream value.',
              'Own performance marketing budgets and allocate investment dynamically across channels, campaigns, products and audiences based on evidence and expected return.',
              'Develop clear acquisition economics across the customer journey, including cost per lead, cost per qualified claim/customer, conversion, expected value and return on marketing investment.',
              'Build robust forecasts and performance plans, identifying opportunities, risks and the investment required to achieve growth objectives.'
            ]
          },
          {
            title: 'Paid Acquisition & Channel Performance',
            items: [
              'Lead strategy and performance across paid search, paid social, display and other measurable acquisition channels relevant to the business.',
              'Continuously optimise campaigns, audiences, bidding, creative, landing experiences and channel mix to improve profitable acquisition.',
              'Ensure channel performance is evaluated using meaningful commercial outcomes rather than platform-reported metrics in isolation.',
              'Identify, test and scale new acquisition opportunities while maintaining disciplined controls around spend, quality and compliance.',
              'Manage relationships with external agencies, media partners and technology providers where appropriate, setting clear performance expectations and holding partners accountable for results.'
            ]
          },
          {
            title: 'Measurement, Attribution & Marketing Intelligence',
            items: [
              'Own the performance marketing measurement framework and establish trusted reporting from media spend through to downstream operational and commercial outcomes.',
              'Work with Data, Product and Technology teams to strengthen tracking, attribution, event measurement and the quality of marketing data.',
              'Build and maintain executive-level reporting covering spend, acquisition, conversion, quality, cohort performance, return and forecast performance.',
              'Use cohort, funnel and channel analysis to identify where value is created or lost across the acquisition and claims journey.',
              'Challenge misleading or incomplete metrics and ensure marketing decisions are grounded in reliable data and commercial reality.'
            ]
          },
          {
            title: 'Conversion Rate Optimisation & Experimentation',
            items: [
              'Own a structured programme of conversion optimisation across landing pages, digital journeys and acquisition funnels.',
              'Develop a clear testing roadmap covering proposition, messaging, creative, forms, eligibility journeys and customer experience.',
              'Partner with Product and Software Engineering to translate performance opportunities into prioritised digital improvements.',
              'Establish disciplined test-and-learn practices with clear hypotheses, success measures and post-test evaluation.',
              'Use customer and operational insight to improve both front-end conversion and the quality of customers entering operational workflows.'
            ]
          },
          {
            title: 'Operational, Product & Compliance Partnership',
            items: [
              'Work closely with Operations to understand capacity, contactability, qualification, case progression and the downstream quality of acquired customers or claims.',
              'Ensure marketing activity can scale responsibly without creating avoidable operational bottlenecks, poor customer outcomes or unnecessary acquisition cost.',
              'Partner with Product and Technology on tracking, digital journeys, automation and platform improvements that support acquisition and conversion.',
              'Work with Compliance and relevant stakeholders to ensure campaigns, claims, promotions, customer journeys and marketing practices meet applicable regulatory and internal standards.',
              'Bring marketing performance insight into wider business planning and help senior leadership make informed decisions about growth, investment and product priorities.'
            ]
          },
          {
            title: 'Leadership & Team Development',
            items: [
              'Build, lead and develop a high-performing performance marketing capability, whether through internal specialists, agencies or a blended model.',
              'Set clear objectives, KPIs and operating rhythms for the function, creating strong accountability for commercial outcomes.',
              'Develop analytical and commercial thinking across the team and create a culture of experimentation, learning and continuous improvement.',
              'Provide clear, concise performance updates and recommendations to senior leadership.'
            ]
          }
        ]
      },
      {
        title: 'Requirements',
        paragraphs: [
          'We are looking for someone who:'
        ],
        items: [
          'Has significant senior-level performance marketing or growth marketing experience in a commercially focused, high-volume consumer business.',
          'Has demonstrable experience managing substantial paid media budgets and delivering measurable improvements in acquisition efficiency and return.',
          'Has deep hands-on understanding of paid search and paid social, together with strong knowledge of broader digital acquisition channels.',
          'Can demonstrate strong commercial understanding of CAC/CPA, funnel conversion, customer or claim value, cohort performance and return on marketing investment.',
          'Is highly analytical and comfortable interrogating data, challenging attribution and turning complex performance information into clear decisions.',
          'Has strong experience with digital analytics, tracking, attribution and performance reporting.',
          'Has experience leading conversion-rate optimisation and structured experimentation across digital acquisition journeys.',
          'Can forecast acquisition performance, manage budgets and make confident investment decisions based on expected commercial return.',
          'Has strong leadership skills and experience managing internal teams, agencies and external partners.',
          'Can work effectively with Operations, Product, Technology, Finance, Data and Compliance stakeholders.',
          'Is commercially curious, highly accountable and comfortable operating in a fast-moving environment where priorities and performance can change quickly.'
        ]
      },
      {
        title: 'Nice to have',
        items: [
          'Experience in claims management, consumer redress, financial services, insurance, legal services, fintech or another regulated consumer sector.',
          'Experience marketing products or services where lead quality and downstream operational conversion matter as much as initial acquisition volume.',
          'Experience with high-volume lead generation and multi-stage eligibility or qualification funnels.',
          'Experience connecting marketing platform data with CRM, workflow or operational data to understand true customer or claim value.',
          'Experience with marketing automation, first-party data strategies and audience segmentation.',
          'Experience operating in a business using automation, data science or AI to improve acquisition, customer journeys or operational decision-making.'
        ]
      },
      {
        title: 'What success looks like',
        items: [
          'Marketing investment is clearly connected to qualified acquisition, downstream conversion and commercial return.',
          'The business can confidently understand which channels, campaigns and customer cohorts create the greatest value.',
          'Acquisition grows without sacrificing lead quality, compliance, customer outcomes or operational capacity.',
          'Performance marketing decisions become increasingly data-led, forecastable and commercially disciplined.',
          'Conversion improves through a consistent programme of testing and digital journey optimisation.',
          'The performance marketing function becomes a trusted growth partner to Operations, Product, Technology and senior leadership.'
        ]
      },
      {
        title: 'Benefits',
        items: [
          'Competitive salary, dependent on experience.',
          '20 days annual leave + bank holidays.',
          'Opportunity to lead and shape the group performance marketing function.',
          'Meaningful ownership of acquisition strategy, media investment and commercial growth.',
          'Work in a technology-led, data-driven business investing in automation, AI-supported workflows and modern systems.',
          'Opportunity to influence digital journeys, customer acquisition and growth strategy across a scaling group.'
        ]
      },
      {
        title: 'Why Join Us?',
        items: [
          'Commercial Impact: Own a function where marketing performance directly influences the growth and economics of the business.',
          'Data-Led Growth: Work in an environment where acquisition, operational performance and technology can be connected to make better commercial decisions.',
          'Real Ownership: Shape the strategy, measurement framework, team and operating model rather than simply inheriting a fixed marketing playbook.',
          'Cross-Functional Influence: Work closely with senior leaders across Operations, Product, Technology, Finance and Compliance to improve the entire customer and claims journey.',
          'Equality, Diversity & Inclusion: We are an equal opportunities employer and are committed to creating an inclusive and supportive working environment. Applications are welcomed from all suitably qualified individuals.'
        ]
      }
    ],
    ctaText: 'If you are a commercially driven performance marketing leader who understands how to turn acquisition investment into measurable business value, we would love to hear from you. Please apply with your CV.',
    note: '*This job specification may be reviewed and updated periodically to reflect the evolving needs of the business.'
  },
  {
    id: 'product-manager-claims',
    company: 'Lloyd James Media Group',
    companies: ['Lloyd James Media Group'],
    title: 'Product Manager (Claims)',
    vacancies: 1,
    meta: 'Wolverhampton | Full-Time | Permanent | £35,000 - £45,000 (DOE)',
    heading: 'Product Manager (Claims)',
    sections: [
      {
        title: 'Shape Your Future with Us',
        paragraphs: [
          'We are a growing, technology-led consumer claims group operating across regulated and non-regulated claims environments. Our businesses specialise in managing high-volume consumer claims processes with a strong focus on operational excellence, compliance, and customer outcomes.',
          'We are investing heavily in automation, data intelligence, and AI-supported workflows to create scalable, efficient, and high-performing claims operations. This role sits at the heart of that operational growth.'
        ]
      },
      {
        title: 'The Opportunity',
        paragraphs: [
          'We are looking for a commercially minded and technically capable Product Manager to take day-to-day ownership of our Car Finance Mis-Selling claims operation. This is a hands-on, mid-management role reporting to the Group Operations Manager, with responsibility for ensuring the product performs effectively today while continuously improving how it operates for the future.',
          'Sitting at the intersection of Operations, Product and Technology, you will own operational performance across the claims journey, lead improvement projects, and work closely with our Software Engineering team to translate operational challenges into scalable system and workflow solutions.',
          'This is not a traditional software Product Manager role. You will combine strong operational management with project delivery, process optimisation and technology-led change. You will need to understand how high-volume processing operations work, be comfortable getting into the detail, and be technically minded enough to collaborate effectively with Software Engineers and other technical stakeholders.'
        ]
      },
      {
        title: "What You'll Be Doing",
        subsections: [
          {
            title: 'Product & Operational Ownership',
            items: [
              'Take day-to-day operational ownership of the Car Finance Mis-Selling claims product and associated processing workflows.',
              'Monitor workflow queues, volumes, throughput, capacity and service levels, ensuring claims progress efficiently through the end-to-end journey.',
              'Identify operational bottlenecks, failure points and exceptions, implementing corrective action and longer-term solutions.',
              'Own and monitor relevant SLAs, KPIs and operational performance measures, escalating risks and driving action where performance falls outside expectations.',
              'Maintain high standards of quality, control and case progression within a regulated claims environment.'
            ]
          },
          {
            title: 'Product Development, Projects & Continuous Improvement',
            items: [
              'Own and deliver operational improvement projects across the Car Finance claims lifecycle, from problem definition and requirements through to implementation and adoption.',
              'Use data, MI and operational insight to identify opportunities to improve throughput, efficiency, quality and customer outcomes.',
              'Build and maintain a prioritised pipeline of process, system and automation improvements, balancing operational impact, risk and delivery effort.',
              'Plan and coordinate change activity, including requirements gathering, process design, UAT, implementation, training and post-launch review.'
            ]
          },
          {
            title: 'Technology & Stakeholder Collaboration',
            items: [
              'Act as the key operational link between the Car Finance claims operation, Group Operations, Software Engineering and other cross-functional teams.',
              'Translate operational problems and business requirements into clear, structured requirements that technical teams can understand and deliver against.'
            ]
          }
        ]
      },
      {
        title: "What We're Looking For",
        items: [
          'Strong experience running day-to-day operational processes within a high-volume processing environment.',
          'Experience taking ownership of an operational function, product, workstream or significant area of delivery.',
          'Technically minded and comfortable working with CRM platforms, workflow systems, automation and technology-enabled processes.',
          'Strong project management skills and able to structure, prioritise and deliver multiple improvement initiatives alongside BAU operational demands.',
          'Confident using KPIs, MI and operational data to understand performance, diagnose issues and make evidence-led decisions.',
          'Able to communicate effectively with both operational and technical stakeholders, including Software Engineers and senior leadership.',
          'Able to translate operational requirements into clear process, system and development requirements and contribute effectively to testing and implementation.',
          'Demonstrable experience delivering process improvement, operational change or transformation initiatives from concept through to implementation.',
          'Proactive, commercially aware, solutions-focused and comfortable challenging existing ways of working.'
        ]
      },
      {
        title: 'Nice to Have',
        items: [
          'Experience within Claims Management, consumer redress, financial services, legal claims or another regulated high-volume processing environment.',
          'Experience within Car Finance claims, complaints or consumer redress.',
          'Experience working directly with Software Engineering or development teams.',
          'Familiarity with agile delivery, user stories, acceptance criteria, UAT or similar product/project delivery practices.'
        ]
      },
      {
        title: 'Benefits',
        items: [
          'Competitive salary',
          '20 days annual leave + bank holidays',
          'Opportunity to own and shape a core claims product and its operational development',
          'Hands-on experience in a tech-enabled, data-driven business',
          'Exposure to automation, AI-supported workflows, and modern systems'
        ]
      },
      {
        title: 'Why Join Us?',
        items: [
          'Career Growth: You will grow your career while being part of a company that is pioneering AI-driven innovation and automation across multiple industries.',
          "Supportive Culture: You'll be part of a collaborative, high-performance team where knowledge-sharing and mentoring are part of our DNA. We offer a fun, inclusive, and fast-paced environment where your ideas are not only heard they're encouraged.",
          'Modern Practice: Work with advanced AI-powered tools, streamlined workflows, and full administrative support.',
          'Strategic Expansion: With ambitious growth plans and a national footprint, we offer exciting long-term opportunities in a dynamic firm.',
          'Equality, Diversity & Inclusion: We are an equal opportunities employer and are committed to creating an inclusive and supportive working environment. Applications are welcomed from all suitably qualified individuals.'
        ]
      }
    ],
    ctaHeading: 'Ready to Take Ownership?',
    ctaText: 'If you are an operationally strong, technically minded problem-solver who enjoys taking ownership, delivering change and improving how complex processes work, we would love to hear from you. Please apply with your CV.',
    note: '*This job specification may be reviewed and updated periodically to reflect the evolving needs of the business.'
  },
  {
    id: 'senior-aws-cloud-devops-engineer',
    company: 'Lloyd James Media Group',
    companies: ['Lloyd James Media Group'],
    title: 'Senior AWS Cloud & DevOps Engineer',
    meta: 'Wolverhampton | Full-Time | Permanent | Technology & Engineering',
    eyebrow: 'TECHNOLOGY & ENGINEERING',
    heading: 'Senior AWS Cloud & DevOps Engineer',
    sections: [
      {
        title: 'Shape your future with us',
        paragraphs: [
          'We are a growing, technology-led consumer claims group operating across regulated and non-regulated claims environments. Our businesses manage high-volume consumer claims processes, supported by modern software, automation, data and AI-enabled workflows.',
          'As our technology estate continues to scale, we are looking for a Senior AWS Cloud & DevOps Engineer to help build, automate, secure and operate the cloud platforms that underpin our applications and operational services.'
        ]
      },
      {
        title: 'The Role',
        paragraphs: [
          'This is a hands-on senior engineering role for someone who combines strong AWS cloud expertise with a DevOps mindset. You will take ownership of cloud infrastructure, deployment automation, platform reliability, observability and operational resilience, working closely with Software Engineers and technology leadership to enable fast, safe and repeatable delivery.',
          'You will be expected to improve the platform as well as operate it: removing manual work, strengthening security and resilience, improving developer experience, controlling cloud cost and helping the engineering function scale effectively.'
        ]
      },
      {
        title: 'Responsibilities',
        subsections: [
          {
            title: 'AWS Cloud Engineering & Platform Operations',
            items: [
              'Design, build, maintain and continuously improve secure, resilient and scalable AWS cloud environments.',
              'Own day-to-day health, availability and performance of cloud infrastructure and production platform services.',
              'Apply AWS best practices across networking, identity and access, compute, storage, databases, backup and recovery.',
              'Design for high availability, fault tolerance, disaster recovery and appropriate business continuity.',
              'Lead technical investigation and resolution of complex cloud and production incidents.'
            ]
          },
          {
            title: 'Infrastructure as Code & Automation',
            items: [
              'Build and maintain infrastructure through Infrastructure as Code, ensuring environments are repeatable, version-controlled and auditable.',
              'Automate provisioning, configuration, deployment and operational tasks to reduce manual intervention and operational risk.',
              'Drive standardisation across environments and improve the consistency of development, test and production platforms.',
              'Identify recurring operational work and engineer sustainable automated solutions.'
            ]
          },
          {
            title: 'CI/CD & Software Delivery',
            items: [
              'Design, maintain and improve CI/CD pipelines that enable reliable and efficient software delivery.',
              'Partner with Software Engineers to improve build, test, release and deployment processes.',
              'Promote deployment practices that support safe releases, rapid rollback and reduced production risk.',
              'Improve developer self-service and remove infrastructure or deployment bottlenecks that slow engineering delivery.'
            ]
          },
          {
            title: 'Observability, Reliability & Incident Management',
            items: [
              'Implement and improve monitoring, logging, alerting and observability across cloud infrastructure and applications.',
              'Define meaningful operational metrics and help establish service reliability objectives and performance baselines.',
              'Participate in incident response, root-cause analysis and post-incident improvement activity.',
              'Use data from production systems to proactively identify reliability, capacity and performance risks before they affect users.'
            ]
          },
          {
            title: 'Cloud Security & Governance',
            items: [
              'Embed security into cloud architecture, infrastructure automation and deployment processes.',
              'Maintain strong identity, access, secrets and permissions controls using least-privilege principles.',
              'Work with technology leadership to manage vulnerabilities, patching, configuration risk and cloud security findings.',
              'Support technical controls, evidence and documentation required in a regulated, data-sensitive business environment.',
              'Ensure appropriate backup, recovery, audit logging and change controls are designed into the platform.'
            ]
          },
          {
            title: 'Cost, Capacity & Continuous Improvement',
            items: [
              'Monitor AWS usage and cost, identify optimisation opportunities and help maintain appropriate cost visibility and accountability.',
              'Plan for growth in application traffic, data volumes and operational workloads.',
              'Evaluate new AWS capabilities and DevOps practices where they offer a clear reliability, security, delivery or cost benefit.',
              'Mentor engineers on cloud and DevOps practices and contribute to technical standards, documentation and engineering ways of working.'
            ]
          }
        ]
      },
      {
        title: 'Requirements',
        paragraphs: [
          'We are looking for someone who:'
        ],
        items: [
          'Has strong commercial experience designing, building and operating production workloads in AWS.',
          'Has substantial hands-on DevOps, Cloud Engineering, Platform Engineering or Site Reliability Engineering experience.',
          'Has strong Infrastructure as Code experience using Terraform, AWS CloudFormation/CDK or comparable tooling.',
          'Has strong experience designing and maintaining CI/CD pipelines and modern software delivery workflows.',
          'Understands AWS networking, IAM, security, monitoring, backup, resilience and disaster recovery principles.',
          'Is comfortable working with containers and modern application deployment patterns.',
          'Can script and automate operational tasks using an appropriate language such as Python, Bash or PowerShell.',
          'Has strong troubleshooting skills across infrastructure, networking, applications and deployment pipelines.',
          'Understands observability and can work effectively with metrics, logs, traces and alerting.',
          'Can collaborate closely with Software Engineers and communicate complex technical issues clearly to technical and non-technical stakeholders.',
          'Takes ownership of production issues and is comfortable operating in a fast-moving environment where reliability matters.',
          'Has a strong security mindset and understands the responsibilities involved in operating systems that process sensitive customer data.'
        ]
      },
      {
        title: 'Nice to have',
        items: [
          'AWS professional or associate-level certification, or equivalent demonstrable AWS expertise.',
          'Experience with container orchestration and managed container platforms.',
          'Experience with Kubernetes and/or AWS container services.',
          'Experience supporting high-volume transactional, workflow or data-processing platforms.',
          'Experience in claims management, financial services, insurance, legal services, fintech or another regulated environment.',
          'Experience with cloud cost optimisation / FinOps practices.',
          'Experience supporting security, compliance or audit requirements in a regulated organisation.'
        ]
      },
      {
        title: 'What success looks like',
        items: [
          'Cloud environments are reliable, secure, observable and capable of scaling with the business.',
          'Deployments become increasingly automated, repeatable and low-risk.',
          'Engineering teams can deliver changes faster without compromising operational stability.',
          'Incidents are resolved effectively and lead to measurable improvements rather than recurring problems.',
          'Manual infrastructure work and operational toil reduce over time.',
          'AWS cost and capacity are visible, understood and actively optimised.'
        ]
      },
      {
        title: 'Benefits',
        items: [
          'Competitive salary, dependent on experience.',
          '20 days annual leave + bank holidays.',
          'Opportunity to take meaningful ownership of a growing AWS cloud estate.',
          'Hands-on involvement in a technology-led, data-driven business with ambitious growth plans.',
          'Exposure to automation, AI-supported workflows and modern engineering practices.',
          'Opportunity to influence cloud architecture, DevOps standards and the future shape of the engineering platform.'
        ]
      },
      {
        title: 'Why Join Us?',
        items: [
          "Career Growth: Take a senior hands-on role in a growing technology function where cloud engineering is central to the company's ability to scale.",
          'Modern Engineering: Work with cloud infrastructure, automation and software delivery practices that directly support high-volume operational products.',
          'Real Ownership: You will have the opportunity to improve how our platforms are built, deployed, monitored and operated - not simply maintain an inherited environment.',
          'Collaborative Culture: Work closely with Software Engineering, Product and Operations to solve real business problems and deliver measurable improvements.',
          'Equality, Diversity & Inclusion: We are an equal opportunities employer and are committed to creating an inclusive and supportive working environment. Applications are welcomed from all suitably qualified individuals.'
        ]
      }
    ],
    ctaText: 'If you are an experienced AWS and DevOps engineer who enjoys taking ownership, automating at scale and building reliable cloud platforms, we would love to hear from you. Please apply with your CV.',
    note: '*This job specification may be reviewed and updated periodically to reflect the evolving needs of the business.'
  },
  {
    id: 'senior-business-analyst-process-automation',
    company: 'Lloyd James Media Group',
    companies: ['Lloyd James Media Group'],
    title: 'Senior Business Analyst & Process Automation',
    meta: 'Wolverhampton | Full-Time | Permanent | Operations, Product & Technology',
    eyebrow: 'OPERATIONS, PRODUCT & TECHNOLOGY',
    heading: 'Senior Business Analyst & Process Automation',
    sections: [
      {
        title: 'Shape your future with us',
        paragraphs: [
          'We are a growing, technology-led consumer claims group operating across regulated and non-regulated claims environments. Our businesses manage high-volume consumer claims processes, supported by modern software, automation, data and AI-enabled workflows.',
          'As we continue to scale, we are looking for a Senior Business Analyst & Process Automation specialist to help us redesign complex operational processes, translate business problems into clear technology requirements and identify opportunities to automate work safely, efficiently and at scale.'
        ]
      },
      {
        title: 'The Role',
        paragraphs: [
          'This is a senior, hands-on role sitting at the intersection of Operations, Product and Technology. You will work across high-volume claims and service processes to understand how work is performed today, identify friction and control points, define better future-state processes and help turn those designs into practical system and automation changes.',
          'You will be expected to move comfortably between operational detail and technical delivery: facilitating discovery, analysing data, mapping end-to-end workflows, defining requirements and acceptance criteria, supporting solution design, coordinating UAT and measuring whether implemented changes deliver the intended outcome.',
          'Process automation is central to the role. You will identify where workflow automation, system integration, rules-based processing and appropriate AI-enabled capabilities can remove manual effort, reduce failure demand, improve quality and increase operational capacity without weakening controls or customer outcomes.'
        ]
      },
      {
        title: 'Responsibilities',
        subsections: [
          {
            title: 'Business Analysis & Process Discovery',
            items: [
              'Lead structured discovery across claims, customer service and back-office processes, working directly with operational teams and subject matter experts.',
              'Map end-to-end current-state processes, including hand-offs, systems, data inputs, decision points, exceptions, controls, rework and customer touchpoints.',
              'Identify root causes behind delays, manual effort, failure demand, poor quality, operational risk and inconsistent outcomes.',
              'Use operational data, MI and direct observation to validate problems and quantify the scale and commercial or operational impact.',
              'Create clear future-state process designs that simplify work, improve control and support scalable growth.'
            ]
          },
          {
            title: 'Requirements & Solution Definition',
            items: [
              'Translate business problems and process designs into clear, structured requirements for Product, Software Engineering, Data and other technical teams.',
              'Develop user stories, business rules, process flows, functional requirements and acceptance criteria at the level of detail required for effective delivery.',
              'Define exception handling, controls, audit requirements and operational fallbacks as part of solution design rather than treating them as afterthoughts.',
              'Challenge unclear requirements and proposed solutions, ensuring teams remain focused on the underlying business outcome rather than simply digitising inefficient processes.',
              'Maintain traceability from problem and requirement through delivery, testing, implementation and measured benefit.'
            ]
          },
          {
            title: 'Process Automation & AI-Enabled Improvement',
            items: [
              'Identify and prioritise automation opportunities across high-volume operational workflows based on value, feasibility, risk and implementation effort.',
              'Assess where rules-based automation, workflow orchestration, system integrations, APIs, document/data processing and AI-enabled capabilities can improve performance.',
              'Work with Engineering and Product teams to shape automation solutions that are reliable, maintainable, observable and appropriate for regulated or controlled processes.',
              'Design human-in-the-loop processes and exception routes where full automation is inappropriate, ensuring clear ownership and controls.',
              'Help build a measurable automation pipeline, tracking benefits such as hours removed, throughput improvement, reduced error/rework, faster cycle time and improved customer outcomes.'
            ]
          },
          {
            title: 'Delivery, Testing & Implementation',
            items: [
              'Work closely with Product Managers, Engineers and operational stakeholders throughout delivery to resolve ambiguity and keep solutions aligned to the intended outcome.',
              'Plan and coordinate business readiness activities including process documentation, UAT, operational acceptance, training inputs and implementation support.',
              'Define practical test scenarios covering happy paths, edge cases, exceptions and operational controls; support users through UAT and defect resolution.',
              'Monitor changes after release, compare actual outcomes against expected benefits and identify follow-up improvements where required.',
              'Support phased rollouts and controlled experimentation where this reduces delivery or operational risk.'
            ]
          },
          {
            title: 'Data, Performance & Continuous Improvement',
            items: [
              'Use data to baseline current performance and establish clear measures of success before changes are implemented.',
              'Analyse process performance, queues, conversion, cycle time, exceptions, error rates and other relevant operational measures to identify improvement opportunities.',
              'Build clear business cases for change, articulating expected benefits, dependencies, risks and implementation effort.',
              'Create repeatable process-improvement methods and documentation standards that improve how the organisation discovers, designs and delivers change.',
              'Act as a constructive challenge to existing ways of working and promote evidence-led continuous improvement across the business.'
            ]
          },
          {
            title: 'Stakeholder Management & Cross-Functional Working',
            items: [
              'Build strong working relationships across Operations, Product, Software Engineering, Data, Compliance and senior leadership.',
              'Facilitate workshops and decision-making sessions, bringing structure to complex or competing stakeholder requirements.',
              'Communicate technical concepts in clear business language and operational requirements in a form technical teams can act upon.',
              'Ensure compliance, customer outcomes, data protection and operational controls are considered throughout process and automation design.',
              'Provide clear updates on scope, decisions, risks, dependencies, delivery progress and realised benefits.'
            ]
          }
        ]
      },
      {
        title: 'What We Are Looking For',
        items: [
          'Strong experience as a Senior Business Analyst, Process Analyst, Automation Analyst, Business Systems Analyst or similar role in a complex operational environment.',
          'Demonstrable experience analysing and redesigning end-to-end business processes rather than documenting requirements in isolation.',
          'Experience translating operational needs into structured requirements, user stories, business rules and acceptance criteria for software or technology teams.',
          'Strong process-mapping capability and confidence facilitating workshops with frontline users, managers, subject matter experts and senior stakeholders.',
          'Experience identifying, defining or delivering process automation, workflow automation, system integration or technology-enabled operational improvement.',
          'Strong analytical skills with the ability to use data and MI to diagnose problems, quantify benefits and evaluate outcomes.',
          'Good technical understanding of systems, workflows, APIs/integrations, data flows and automation concepts; coding experience is not essential, but technical curiosity is.',
          'Experience supporting UAT, implementation and post-launch benefit measurement.',
          'Excellent communication, documentation, problem-solving and stakeholder-management skills.',
          'Comfortable working in a fast-moving environment where priorities evolve and where you are expected to challenge, simplify and improve.'
        ]
      },
      {
        title: 'Nice to Have',
        items: [
          'Experience within claims management, consumer redress, financial services, insurance, legal services or another regulated high-volume processing environment.',
          'Experience with BPM/workflow platforms, low-code/no-code automation, RPA or integration-led automation.',
          'Experience applying AI, machine learning or intelligent document/data processing to operational workflows, with appropriate human oversight and controls.',
          'Familiarity with SQL, BI/reporting tools, process-mining tools or other analytical techniques used to understand operational performance.',
          'Experience working in agile product or software delivery environments alongside Product Managers and Software Engineers.',
          'Formal Business Analysis, Lean, Six Sigma, process improvement or project/change qualifications.'
        ]
      },
      {
        title: 'Benefits',
        items: [
          'Competitive salary, dependent on experience.',
          '20 days annual leave plus bank holidays.',
          'Opportunity to influence automation and process design across a growing group of technology-enabled consumer claims businesses.',
          'Direct exposure to senior Operations, Product and Engineering leadership and the opportunity to see improvements move from discovery through to production.',
          'A modern, data-led environment investing in automation, AI-supported workflows and scalable technology.'
        ]
      },
      {
        title: 'Why Join Us?',
        items: [
          'Career Growth & AI Innovation: Join a growing organisation investing in automation, data and intelligent workflows.',
          'Real Operational Impact: Work on high-volume processes where better design and automation can materially improve capacity, quality and customer outcomes.',
          'Cross-Functional Influence: Operate at the point where business problems become product, engineering and automation priorities.',
          'Modern Practice: Help shape how we analyse, redesign and automate processes as the organisation scales.',
          'Strategic Expansion: Be part of a business investing in technology-enabled growth across consumer claims and related services.'
        ]
      },
      {
        title: 'Equality, Diversity & Inclusion',
        paragraphs: [
          'We are committed to building an inclusive workplace where everyone is treated fairly and with respect. We welcome applications from all backgrounds and make employment decisions based on skills, experience and potential.'
        ]
      }
    ],
    ctaText: 'If you are an analytical, technically minded problem-solver who enjoys getting into the detail of complex processes and turning operational problems into measurable technology-enabled improvements, we would love to hear from you. Please apply with your CV.',
    note: 'This job description may be reviewed as the business evolves and is intended to describe the main responsibilities of the role rather than every individual task.'
  }
];

const COMPANY_OPTIONS = [
  ...new Set(CAREER_JOBS.flatMap((job) => job.companies || [job.company]))
];

const ALL_COMPANIES_VALUE = 'all';

const getJobPositionLabel = (job) => `${job.title} - ${job.company}`;

const Careers = () => {
  const [selectedCompany, setSelectedCompany] = useState(ALL_COMPANIES_VALUE);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [positionPrefillKey, setPositionPrefillKey] = useState(0);

  const filteredJobs = selectedCompany === ALL_COMPANIES_VALUE ? CAREER_JOBS : CAREER_JOBS.filter((job) => {
    const companies = job.companies || [job.company];
    return companies.includes(selectedCompany);
  });
  const totalJobsPosted = filteredJobs.length;
  const [expandedJobId, setExpandedJobId] = useState('');

  const handleJobToggle = (jobId) => {
    setExpandedJobId(prevJobId => prevJobId === jobId ? '' : jobId);
  };

  const handleCompanyChange = (e) => {
    const company = e.target.value;

    setSelectedCompany(company);
    setExpandedJobId('');
  };

  const handleApplyNow = (job) => {
    setSelectedPosition(getJobPositionLabel(job));
    setPositionPrefillKey(prevKey => prevKey + 1);
  };

  return (
    <div className="careers bg-[#f8f9fa]">
      <SEO
        title="Careers - Join Our Team at Lloyd James Media"
        description="Build your career with Lloyd James Media. Explore current opportunities and submit your application to our HR team."
        keywords="careers Lloyd James, Lloyd James jobs, data consultancy jobs, business intelligence careers, marketing analytics jobs"
        ogUrl={`${import.meta.env.VITE_SITE_URL || 'https://www.lloydjames.com'}/careers`}
      />

      <section
        className="relative flex min-h-[360px] items-center bg-cover bg-center bg-no-repeat px-4 py-16 sm:min-h-[440px]"
        style={{ backgroundImage: "url('/images/home-banner-1.webp')" }}
      >
        <div className="absolute inset-0 bg-black/65" />
        <Container className="relative z-10">
          <div className="max-w-3xl text-white">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: '#db0058' }}>
              Careers
            </p>
            <h1 className="mb-5 text-4xl font-normal leading-tight sm:text-5xl md:text-6xl">
              {CAREERS_CONTENT.title}
            </h1>
            <div className="space-y-4">
              {CAREERS_CONTENT.introParagraphs.map((paragraph) => (
                <p key={paragraph} className="max-w-2xl text-base leading-relaxed text-gray-200 sm:text-lg">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <section
        className="px-4 py-10 sm:py-14 lg:py-16"
        style={{ backgroundColor: COLORS.secondary }}
      >
        <Container>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-7">
              <div className="mb-6 text-white">
                <h2 className="mb-3 text-3xl font-bold sm:text-4xl">
                  Current opportunities
                </h2>
                <p className="max-w-2xl text-gray-300">
                  Select a company to view current job vacancies and open the full job description.
                </p>
                <p className="mt-4 inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white">
                  {totalJobsPosted} opening{totalJobsPosted !== 1 && 's'} available
                </p>
              </div>

              <label className="mb-6 block">
                <span className="mb-2 block text-sm font-semibold text-white">Select company</span>
                <select
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  className="w-full rounded border-2 border-[#db0058] bg-[#2f3134] px-3 py-3 text-sm text-white outline-none transition-colors focus:border-[#b8004a]"
                >
                  <option value={ALL_COMPANIES_VALUE}>All</option>
                  {COMPANY_OPTIONS.map((company) => (
                    <option key={company} value={company}>
                      {company}
                    </option>
                  ))}
                </select>
              </label>

              <div
                className="career-job-scroll space-y-4"
                role="region"
                aria-label="Current job openings"
                tabIndex={0}
              >
                {filteredJobs.map((job) => (
                  <JobListing
                    key={job.id}
                    job={job}
                    isExpanded={expandedJobId === job.id}
                    onToggle={handleJobToggle}
                    onApplyNow={handleApplyNow}
                  />
                ))}

                {filteredJobs.length === 0 && (
                  <div className="rounded-lg border border-white/10 bg-white p-6 text-gray-700">
                    <h3 className="mb-2 text-lg font-bold text-gray-900">No current vacancies</h3>
                    <p>
                      There are no open roles listed for {selectedCompany} right now. You can still submit a speculative application using the form.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div id="careers-application" className="lg:col-span-5">
              <ApplicationForm
                positionOptions={filteredJobs.map(getJobPositionLabel)}
                selectedPosition={selectedPosition}
                positionPrefillKey={positionPrefillKey}
              />
            </div>
          </div>
        </Container>
      </section>

      <section className="px-4 py-10" style={{ backgroundColor: COLORS.primary }}>
        <Container>
          <div className="grid grid-cols-1 gap-8 text-white lg:grid-cols-2">
            <div>
              <h2 className="mb-4 text-2xl font-bold sm:text-3xl">{CAREERS_CONTENT.whyWorkWithUsTitle}</h2>
              <p className="text-base leading-relaxed sm:text-lg">
                {CAREERS_CONTENT.whyWorkWithUs}
              </p>
            </div>
            <div>
              <h2 className="mb-4 text-2xl font-bold sm:text-3xl">{CAREERS_CONTENT.lifeInBusinessTitle}</h2>
              <div className="space-y-4 text-base leading-relaxed sm:text-lg">
                <p>{CAREERS_CONTENT.testimonial}</p>
                {CAREERS_CONTENT.lifeInBusinessParagraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Careers;
